import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, filter, from, map, switchMap } from 'rxjs';
import type { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson';
import { SKIP_LOADER } from '../auth/loader.interceptor';
import { GlobalService } from './global.service';

export interface GeoParseResponse {
  success?: boolean;
  message?: string;
  error?: string;
  id?: string;
  filename?: string;
  features?: number;
  missing_files?: string[];
  geojson?: FeatureCollection<Geometry, GeoJsonProperties>;
}

export interface GeoServiceFinding {
  code: string;
  featureIndex?: number | null;
  sourceFeature?: number | null;
  at?: [number, number] | null;
}

export interface TracesGeometryResponse {
  geoJson?: string;
  findings?: GeoServiceFinding[];
}

export interface GeometryValidationResponse {
  findings?: GeoServiceFinding[];
}

@Injectable({ providedIn: 'root' })
export class GeoUploadService {
  private readonly http = inject(HttpClient);
  private readonly global = inject(GlobalService);
  private readonly withoutLoader = { context: new HttpContext().set(SKIP_LOADER, true) };

  parseFileToGeoJSON(file: File, onProgress?: (pct: number) => void): Observable<GeoParseResponse> {
    return this.geoApi('geo/parse').pipe(
      switchMap((url) =>
        this.http.post<GeoParseResponse>(url, fileForm(file), {
          ...this.withoutLoader,
          reportProgress: true,
          observe: 'events',
        })
      ),
      map((event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          onProgress?.(Math.round((event.loaded * 100) / event.total));
        }
        return event instanceof HttpResponse ? (event.body as GeoParseResponse) : undefined;
      }),
      filter((response): response is GeoParseResponse => response !== undefined)
    );
  }

  tracesGeometry(geojson: FeatureCollection): Observable<TracesGeometryResponse> {
    return this.geoApi('geo/traces/geometry').pipe(
      switchMap((url) => this.http.post<TracesGeometryResponse>(url, geojson, this.withoutLoader))
    );
  }

  validateGeometry(geojson: FeatureCollection): Observable<GeometryValidationResponse> {
    return this.geoApi('geo/validate/geometry').pipe(
      switchMap((url) => this.http.post<GeometryValidationResponse>(url, geojson, this.withoutLoader))
    );
  }

  private geoApi(path: string): Observable<string> {
    return from(this.global.services()).pipe(map(({ geoServiceUrl }) => `${geoServiceUrl}/api/${path}`));
  }
}

function fileForm(file: File): FormData {
  const form = new FormData();
  form.append('file', file, file.name);
  return form;
}
